import { CustomAnalyticsQueryVersion } from "@redotech/redo-analytics-custom-analytics/query/custom-analytics-report-query";
import { AnalyticsValueNameSchema } from "@redotech/redo-analytics-custom-analytics/query/v2/analytics-query/analytics-query-refs";
import { AnalyticsColumnSchema } from "@redotech/redo-analytics-custom-analytics/semantic-queries/v1/analytics-query/analytics-query-schema";
import { assertNever } from "@redotech/util/type";
import * as z from "zod";
import {
  CustomAnalyticsReportColumnPresentationsSchema,
  CustomAnalyticsReportColumnSchema,
} from "./custom-analytics-report";

export const CUSTOM_ANALYTICS_REPORT_COLUMN_DEFINITION_LIMIT = 45;

export const CustomAnalyticsReportV1ColumnDefinitionSchema = z.object({
  version: z.literal(CustomAnalyticsQueryVersion.V1).optional(),
  columns: z
    .array(AnalyticsColumnSchema)
    .min(1)
    .max(CUSTOM_ANALYTICS_REPORT_COLUMN_DEFINITION_LIMIT),
  columnPresentation: CustomAnalyticsReportColumnPresentationsSchema,
});
export interface CustomAnalyticsReportV1ColumnDefinition extends z.infer<
  typeof CustomAnalyticsReportV1ColumnDefinitionSchema
> {}

export const CustomAnalyticsReportV2ColumnSchema = z.object({
  name: AnalyticsValueNameSchema,
  label: z.string().optional(),
  kind: z.enum(["metric", "detail"]),
});
export interface CustomAnalyticsReportV2Column extends z.infer<
  typeof CustomAnalyticsReportV2ColumnSchema
> {}

export const CustomAnalyticsReportV2ColumnDefinitionSchema = z.object({
  version: z.literal(CustomAnalyticsQueryVersion.V2),
  columns: z
    .array(CustomAnalyticsReportV2ColumnSchema)
    .min(1)
    .max(CUSTOM_ANALYTICS_REPORT_COLUMN_DEFINITION_LIMIT),
  columnPresentation: CustomAnalyticsReportColumnPresentationsSchema,
});
export interface CustomAnalyticsReportV2ColumnDefinition extends z.infer<
  typeof CustomAnalyticsReportV2ColumnDefinitionSchema
> {}

export const CustomAnalyticsReportColumnDefinitionSchema = z.union([
  CustomAnalyticsReportV2ColumnDefinitionSchema,
  CustomAnalyticsReportV1ColumnDefinitionSchema,
]);
export type CustomAnalyticsReportColumnDefinition =
  | CustomAnalyticsReportV1ColumnDefinition
  | CustomAnalyticsReportV2ColumnDefinition;

export const CustomAnalyticsReportColumnDraftSchema = z.object({
  definition: CustomAnalyticsReportColumnDefinitionSchema,
  expectedUpdatedAt: z.date(),
});
export interface CustomAnalyticsReportColumnDraft extends z.infer<
  typeof CustomAnalyticsReportColumnDraftSchema
> {}

export const CustomAnalyticsReportColumnEditorSchema = z.object({
  definition: CustomAnalyticsReportColumnDefinitionSchema,
  columnsMetadata: z.array(CustomAnalyticsReportColumnSchema),
});
export interface CustomAnalyticsReportColumnEditor extends z.infer<
  typeof CustomAnalyticsReportColumnEditorSchema
> {}

export const CustomAnalyticsReportCatalogEntrySchema = z.object({
  id: z.string(),
  label: z.string(),
  category: z.string(),
  sourceLabel: z.string(),
  selectedSourceNames: z.array(z.string()).default([]),
  description: z.string(),
  kind: z.enum(["metric", "detail"]),
  effect: z.string(),
  searchTerms: z.array(z.string()),
  availability: z.enum(["available", "selected", "unavailable"]),
  reason: z.string().optional(),
  reasonCode: z
    .enum(["column_limit", "incompatible", "unsupported"])
    .optional(),
  capabilities: z.object({
    filterSource: z.enum(["source", "projection_source_only", "unsupported"]),
    filterResult: z.boolean(),
    sort: z.boolean(),
    group: z.boolean(),
  }),
});
export interface CustomAnalyticsReportCatalogEntry extends z.infer<
  typeof CustomAnalyticsReportCatalogEntrySchema
> {}

export class InvalidCustomAnalyticsReportColumnDraftError extends Error {}

export class UnavailableCustomAnalyticsReportColumnError extends InvalidCustomAnalyticsReportColumnDraftError {}

export function requireV1CustomAnalyticsReportColumnDefinition(
  definition: CustomAnalyticsReportColumnDefinition,
): CustomAnalyticsReportV1ColumnDefinition {
  switch (definition.version) {
    case undefined:
    case CustomAnalyticsQueryVersion.V1:
      return definition;
    case CustomAnalyticsQueryVersion.V2:
      throw new InvalidCustomAnalyticsReportColumnDraftError(
        "The report columns changed. Refresh and try again.",
      );
    default:
      return assertNever(definition);
  }
}

export function requireV2CustomAnalyticsReportColumnDefinition(
  definition: CustomAnalyticsReportColumnDefinition,
): CustomAnalyticsReportV2ColumnDefinition {
  switch (definition.version) {
    case undefined:
    case CustomAnalyticsQueryVersion.V1:
      throw new InvalidCustomAnalyticsReportColumnDraftError(
        "The report columns changed. Refresh and try again.",
      );
    case CustomAnalyticsQueryVersion.V2:
      return definition;
    default:
      return assertNever(definition);
  }
}

export function selectCustomAnalyticsReportColumnDefinition({
  definition,
  sourceNames,
}: {
  definition: CustomAnalyticsReportColumnDefinition;
  sourceNames: readonly string[];
}): CustomAnalyticsReportColumnDefinition {
  const indices = sourceNames.map((name) =>
    definition.columnPresentation.findIndex(
      ({ sourceName }) => sourceName === name,
    ),
  );
  const columnPresentation = selectByIndices(
    definition.columnPresentation,
    indices,
  );
  switch (definition.version) {
    case undefined:
    case CustomAnalyticsQueryVersion.V1:
      return {
        ...definition,
        columns: selectByIndices(definition.columns, indices),
        columnPresentation,
      };
    case CustomAnalyticsQueryVersion.V2:
      return {
        ...definition,
        columns: selectByIndices(definition.columns, indices),
        columnPresentation,
      };
    default:
      return assertNever(definition);
  }
}

export function getCustomAnalyticsReportColumnDefinitionKind({
  definition,
  index,
}: {
  definition: CustomAnalyticsReportColumnDefinition;
  index: number;
}): CustomAnalyticsReportV2Column["kind"] {
  switch (definition.version) {
    case undefined:
    case CustomAnalyticsQueryVersion.V1:
      return definition.columns[index]?.type === "metric" ? "metric" : "detail";
    case CustomAnalyticsQueryVersion.V2:
      return definition.columns[index]?.kind ?? "detail";
    default:
      return assertNever(definition);
  }
}

function selectByIndices<T>(values: readonly T[], indices: readonly number[]) {
  return indices.map((index) => {
    const value = values[index];
    if (value === undefined) {
      throw new InvalidCustomAnalyticsReportColumnDraftError(
        "The report columns changed. Refresh and try again.",
      );
    }
    return value;
  });
}
